import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagContext';
import { isModuleAccessible, moduleInUserStages, STAGE_SCOPED_ROLES } from '../config/moduleConfig';

// "Is this person allowed to see this destination?" — the sidebar's answer, in one
// place, so a page whose tabs stand in for nav entries reaches the same verdict.
//
// The Processing Overview merged three sidebar entries (Overview, Workflow Board,
// Production) into one page with three tabs. Each tab still has to be gated the way
// its nav entry was, and re-typing the precedence here would be the second copy of a
// rule that has already been got wrong once (#4 used to run before #3, so an explicit
// role-matrix grant was filtered out before its override could fire).
//
// Precedence, highest first:
//   1. Tenant feature flag — kills the feature entirely for this client
//   2. Per-user page_permissions allowlist (if set)
//   3. Role-feature matrix from super-admin (explicit true/false per role)
//   4. Hardcoded moduleConfig.roles fallback
//
// An item is `{ moduleKey, featureCode, pageKeys?, anyOf? }`. `pageKeys` is for
// rollup entries like Masters, which stands for a set of pages and is visible when
// the user holds any one of them. `anyOf` is for an entry that opens a page of
// tabs — Processing → Overview — and is worth showing as long as one tab is.
export default function useNavVisibility() {
  const { user } = useAuth();
  const { features, roleFeatures, isEnabled, isRoleEnabled, loading: featuresLoading } = useFeatureFlags();

  const hasFeatures = Object.keys(features || {}).length > 0;
  // Gate is active once we have any flag data (from event, cache, or /auth/me).
  // Mid-load with nothing cached, treat it as active so nothing flashes into view.
  const flagsGateActive = hasFeatures || (featuresLoading && !hasFeatures);

  const canSee = useCallback(function check(item) {
    if (!item) return false;
    if (Array.isArray(item.anyOf)) return item.anyOf.some(check);
    if (!item.moduleKey) return true;

    // 1. Tenant feature flag (master switch)
    if (item.featureCode && flagsGateActive && !isEnabled(item.featureCode)) return false;

    // admin / owner / super_admin always see everything past the feature flag.
    if (user?.role === 'admin' || user?.role === 'owner' || user?.role === 'super_admin') return true;

    // 1b. Stage scope. A data-entry person is hired for one part of the plant, so
    //     their sidebar is narrowed to the stages on their profile. Deliberately
    //     placed *above* the role-matrix branch below: a super-admin grant on a
    //     feature code must not hand a procurement operator the production floor,
    //     which is what would happen if this ran as another fallback. It only ever
    //     narrows — see moduleInUserStages — so it cannot become a second way in.
    if (!moduleInUserStages(item.moduleKey, user)) return false;

    // 2. Per-user allowlist. Skipped for stage-scoped roles: stage assignment
    //    (1b above) is already the whole-module gate for them — a data-entry
    //    person is meant to reach every screen of their assigned stage, with
    //    edits/deletes held for approval, not a hand-picked subset of pages.
    //    A leftover page_permissions array (e.g. from before a stage was
    //    assigned) would otherwise silently narrow them below their stage.
    const userPagePermissions = user?.page_permissions;
    if (
      !STAGE_SCOPED_ROLES.includes(user?.role) &&
      Array.isArray(userPagePermissions) && userPagePermissions.length > 0
    ) {
      if (Array.isArray(item.pageKeys)) {
        return item.pageKeys.some((key) => userPagePermissions.includes(key));
      }
      return userPagePermissions.includes(item.moduleKey);
    }

    // 3. Role-feature matrix — explicit grant or deny wins over the static
    //    moduleConfig.roles list. Only consults the matrix when it's both loaded
    //    AND has an entry for this feature code (no entry → fall through to the
    //    moduleKey default).
    const hasRoleMatrix = roleFeatures && Object.keys(roleFeatures).length > 0;
    if (hasRoleMatrix && item.featureCode && Object.prototype.hasOwnProperty.call(roleFeatures, item.featureCode)) {
      return isRoleEnabled(item.featureCode);
    }

    // 4. Hardcoded moduleConfig.roles fallback (the historical default)
    return isModuleAccessible(item.moduleKey, user?.role);
  }, [user, flagsGateActive, isEnabled, roleFeatures, isRoleEnabled]);

  return { canSee, flagsGateActive };
}
